import { useEffect, useRef, useCallback, useState } from 'react';
import { cn } from '@/lib/utils';
import { generateId, shuffleArray } from '@/lib/utils';
import { useTranslation } from '@/i18n';
import { useGameScale, GAME_DESIGN } from '@/hooks/useGameScale';
import type { GameState, FallingPart, Slot, PartDefinition } from './types';
import { GAME_CONFIG, PART_DEFINITIONS } from './types';

interface GameScreenProps {
  gameState: GameState;
  onGameEnd: (state: GameState, won: boolean) => void;
  updateGameState: (updates: Partial<GameState>) => void;
}

// Design constants for trash bin (in design pixels)
const TRASH_BIN_DESIGN = {
  width: 130,
  height: 160,
  left: 30,
  bottom: 30,
};

// HUD height in design pixels
const HUD_HEIGHT_DESIGN = 70;

// Retro Trap zone design dimensions
const RETRO_TRAP_DESIGN = {
  width: 160,
  height: 160,
  topPercent: 45,
};

export function GameScreen({ gameState, onGameEnd, updateGameState }: GameScreenProps) {
  const screenRef = useRef<HTMLDivElement>(null);
  const gameStateRef = useRef(gameState);
  const [parts, setParts] = useState<FallingPart[]>([]);
  const [slots, setSlots] = useState<Slot[]>([]);
  const [effects, setEffects] = useState<Array<{ id: string; x: number; y: number; text: string; isGood: boolean }>>([]);
  const [banner, setBanner] = useState<string | null>(null);
  const [draggingTrashItem, setDraggingTrashItem] = useState(false);
  const [retroMessage, setRetroMessage] = useState<string | null>(null);
  const [trapHover, setTrapHover] = useState(false);
  
  const shuffledPartsRef = useRef<PartDefinition[]>([]);
  const spawnIndexRef = useRef(0);
  const lastSpawnTimeRef = useRef(0);
  const animationFrameRef = useRef<number | null>(null);
  const gameInitializedRef = useRef(false);
  
  const dragStateRef = useRef<{
    part: FallingPart | null;
    offsetX: number;
    offsetY: number;
  }>({ part: null, offsetX: 0, offsetY: 0 });
  const partsRef = useRef<FallingPart[]>([]);

  const { t } = useTranslation();
  
  // Responsive scaling - use parent container for measurement
  const containerRef = useRef<HTMLDivElement>(null);
  const { scaleValue } = useGameScale(containerRef, GAME_DESIGN.width, GAME_DESIGN.height);

  // Keep refs in sync
  useEffect(() => {
    gameStateRef.current = gameState;
  }, [gameState]);

  useEffect(() => {
    partsRef.current = parts;
  }, [parts]);

  // Initialize slots (store design values, scale during render)
  useEffect(() => {
    const normalParts = PART_DEFINITIONS.filter(p => p.type !== 'trash_item');
    const newSlots: Slot[] = normalParts.map(def => ({
      id: def.id,
      acceptId: def.id,
      x: def.slotPos.x, // percentage
      y: def.slotPos.y, // percentage
      width: def.slotSize.w, // design pixels - will be scaled during render
      height: def.slotSize.h, // design pixels - will be scaled during render
      isFilled: false,
      isHighlighted: false,
      isAnimating: false,
    }));
    setSlots(newSlots);
  }, []);

  // Prepare deck - called once when game starts
  const prepareDeck = useCallback(() => {
    const normalParts = PART_DEFINITIONS.filter(p => p.type !== 'trash_item');
    const trashPart = PART_DEFINITIONS.find(p => p.type === 'trash_item');
    
    const deck = [...normalParts];
    if (trashPart) {
      for (let i = 0; i < 5; i++) deck.push(trashPart);
    }
    
    shuffledPartsRef.current = shuffleArray(deck);
    spawnIndexRef.current = 0;
    return deck.length;
  }, []);

  // Spawn part - using ref for scale to avoid dependency issues
  const scaleValueRef = useRef(scaleValue);
  useEffect(() => {
    scaleValueRef.current = scaleValue;
  }, [scaleValue]);

  const spawnPart = useCallback(() => {
    const screen = screenRef.current;
    if (!screen || gameStateRef.current.status !== 'playing' || gameStateRef.current.isPaused) return;
    if (spawnIndexRef.current >= shuffledPartsRef.current.length) return;
    
    // Use ref to check parts count to avoid dependency on parts state
    if (partsRef.current.filter(p => !p.isPlaced).length >= GAME_CONFIG.maxOnScreen) return;

    const def = shuffledPartsRef.current[spawnIndexRef.current];
    if (!def) return;
    spawnIndexRef.current++;

    const screenWidth = screen.offsetWidth;
    // Scale part dimensions based on current scale factor
    const currentScaleValue = scaleValueRef.current;
    const width = currentScaleValue(def.partSize.w);
    const height = currentScaleValue(def.partSize.h);
    const x = Math.random() * (screenWidth - width);

    const newPart: FallingPart = {
      id: generateId(),
      definition: def,
      x,
      y: -height,
      width,
      height,
      isDragging: false,
      isPlaced: false,
      spawnTime: performance.now(),
    };

    setParts(prev => [...prev, newPart]);
  }, []); // No dependencies - uses refs internally

  // Game initialization - only runs once when game starts
  useEffect(() => {
    if (gameState.status !== 'playing') {
      gameInitializedRef.current = false;
      return;
    }

    // Only initialize once per game session
    if (gameInitializedRef.current) return;
    gameInitializedRef.current = true;

    const totalParts = prepareDeck();
    updateGameState({ totalParts });
    
    // Reset slots
    setSlots(prev => prev.map(s => ({ ...s, isFilled: false, isHighlighted: false })));
    setParts([]);
    
    // Show banner
    setBanner('Installing without uKonn-X');
    setTimeout(() => setBanner(null), 1500);
  }, [gameState.status, prepareDeck, updateGameState]);

  // Level 0 -> Level 1 transition timer
  useEffect(() => {
    if (gameState.status !== 'playing' || gameState.level !== 0) return;
    
    const level0Timer = setTimeout(() => {
      gameStateRef.current = { ...gameStateRef.current, level: 1 };
      updateGameState({ level: 1 });
      setBanner('Installing with uKonn-X');
      setTimeout(() => setBanner(null), 1500);
    }, GAME_CONFIG.level0DurationMs);

    return () => clearTimeout(level0Timer);
  }, [gameState.status, gameState.level, updateGameState]);

  // Game loop - runs continuously while playing
  useEffect(() => {
    if (gameState.status !== 'playing') return;

    let lastTime = performance.now();
    let running = true;
    
    const gameLoop = (currentTime: number) => {
      if (!running || gameStateRef.current.status !== 'playing') return;
      if (gameStateRef.current.isPaused) {
        lastTime = currentTime; // Prevent large delta when resuming
        animationFrameRef.current = requestAnimationFrame(gameLoop);
        return;
      }

      const deltaTime = (currentTime - lastTime) / 1000;
      lastTime = currentTime;

      // Spawn logic
      if (currentTime - lastSpawnTimeRef.current > GAME_CONFIG.spawnIntervalMs) {
        spawnPart();
        lastSpawnTimeRef.current = currentTime;
      }

      // Update falling parts with scaled fall speed
      const screenHeight = screenRef.current?.offsetHeight || 800;
      const speedFactor = gameStateRef.current.level === 0 ? 1.0 : (1 + 0.2 * (gameStateRef.current.speedStage - 1));
      // Scale fall speed proportionally to screen size
      const currentScale = screenRef.current ? screenRef.current.offsetHeight / GAME_DESIGN.height : 1;
      const fallSpeed = GAME_CONFIG.fallSpeedPxPerSec * speedFactor * currentScale;

      setParts(prev => {
        const updated = prev.map(part => {
          if (part.isDragging || part.isPlaced) return part;
          
          const newY = part.y + fallSpeed * deltaTime;
          
          // Check if fell off screen
          if (newY > screenHeight) {
            // Handle miss
            const newMisses = gameStateRef.current.missesCount + 1;
            updateGameState({ missesCount: newMisses });
            
            // Re-add to deck
            shuffledPartsRef.current.push(part.definition);
            updateGameState({ totalParts: shuffledPartsRef.current.length });
            
            // Check game over
            if (newMisses >= GAME_CONFIG.maxAllowedMisses && gameStateRef.current.level > 0) {
              onGameEnd(gameStateRef.current, false);
            }
            
            return { ...part, isPlaced: true }; // Mark for removal
          }
          
          return { ...part, y: newY };
        });
        
        return updated.filter(p => !(p.isPlaced && p.y > screenHeight));
      });

      // Check win condition
      const placed = gameStateRef.current.placedCount;
      const total = shuffledPartsRef.current.length;
      if (placed >= total && total > 0 && partsRef.current.filter(p => !p.isPlaced).length === 0) {
        onGameEnd(gameStateRef.current, true);
        return;
      }

      animationFrameRef.current = requestAnimationFrame(gameLoop);
    };

    // Initial spawn after a small delay to ensure screen is measured
    setTimeout(() => {
      if (running && gameStateRef.current.status === 'playing') {
        spawnPart();
        lastSpawnTimeRef.current = performance.now();
        animationFrameRef.current = requestAnimationFrame(gameLoop);
      }
    }, 100);

    return () => {
      running = false;
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [gameState.status, spawnPart, onGameEnd, updateGameState]);

  // Drag handlers - use partsRef to avoid stale closure
  const handleDragStart = useCallback((partId: string, clientX: number, clientY: number) => {
    // Use partsRef for fresh data
    const part = partsRef.current.find(p => p.id === partId);
    if (!part || part.isPlaced || part.isDragging || gameStateRef.current.status !== 'playing') return;

    const screen = screenRef.current;
    if (!screen) return;

    // First interaction transitions from Level 0 to Level 1 (real game starts!)
    if (gameStateRef.current.level === 0) {
      // Update both state AND ref immediately so trap detection works in this drag
      gameStateRef.current = { ...gameStateRef.current, level: 1 };
      updateGameState({ level: 1 });
      setBanner('Installing with uKonn-X');
      setTimeout(() => setBanner(null), 1500);
    }

    const rect = screen.getBoundingClientRect();
    const x = clientX - rect.left;
    const y = clientY - rect.top;

    // Store part ID only, not the whole object (to avoid stale data)
    dragStateRef.current = {
      part: { ...part }, // Make a copy with current values
      offsetX: x - part.x,
      offsetY: y - part.y,
    };

    // Set isDragging immediately and synchronously
    setParts(prev => prev.map(p => 
      p.id === partId ? { ...p, isDragging: true } : p
    ));

    // Track if dragging trash item (for UI highlighting)
    if (part.definition.type === 'trash_item') {
      setDraggingTrashItem(true);
    } else {
      // Highlight matching slot (works in all levels)
      setSlots(prev => prev.map(s => ({
        ...s,
        isHighlighted: s.acceptId === part.definition.id && !s.isFilled,
      })));
    }
  }, [updateGameState]); // Added updateGameState dependency

  const handleDragMove = useCallback((clientX: number, clientY: number) => {
    const { part } = dragStateRef.current;
    if (!part) return;

    const screen = screenRef.current;
    if (!screen) return;

    // Check if part still exists
    const partExists = partsRef.current.some(p => p.id === part.id);
    if (!partExists) {
      // Part was removed, clear drag state
      dragStateRef.current = { part: null, offsetX: 0, offsetY: 0 };
      setSlots(prev => prev.map(s => ({ ...s, isHighlighted: false })));
      setTrapHover(false);
      return;
    }

    const rect = screen.getBoundingClientRect();
    const x = clientX - rect.left - dragStateRef.current.offsetX;
    const y = clientY - rect.top - dragStateRef.current.offsetY;

    setParts(prev => prev.map(p => 
      p.id === part.id ? { ...p, x, y } : p
    ));

    // Check trap hover for trash items (like original)
    if (part.definition.type === 'trash_item' && gameStateRef.current.level > 0) {
      const trapElement = screen.querySelector('[data-retro-trap]') as HTMLElement;
      if (trapElement) {
        const partElement = screen.querySelector(`[data-part-id="${part.id}"]`) as HTMLElement;
        if (partElement) {
          const partRect = partElement.getBoundingClientRect();
          const trapRect = trapElement.getBoundingClientRect();
          const partCenterX = partRect.left + partRect.width / 2;
          const partCenterY = partRect.top + partRect.height / 2;
          const isOverTrap = partCenterX >= trapRect.left && partCenterX <= trapRect.right &&
                             partCenterY >= trapRect.top && partCenterY <= trapRect.bottom;
          setTrapHover(isOverTrap);
        }
      }
    }
  }, []);

  const handleDragEnd = useCallback(() => {
    const { part: draggedPart } = dragStateRef.current;
    
    // ALWAYS reset highlights first, regardless of part state
    setSlots(prev => prev.map(s => ({ ...s, isHighlighted: false })));
    setDraggingTrashItem(false);
    setTrapHover(false);
    
    // Clear drag state early to prevent issues
    const partId = draggedPart?.id;
    dragStateRef.current = { part: null, offsetX: 0, offsetY: 0 };
    
    if (!partId) return;

    const screen = screenRef.current;
    if (!screen) return;

    // Get the CURRENT part from state
    const currentPart = partsRef.current.find(p => p.id === partId);
    if (!currentPart) {
      // Part no longer exists - just reset isDragging for any stuck parts
      setParts(prev => prev.map(p => ({ ...p, isDragging: false })));
      return;
    }

    // Find the actual DOM element for accurate position (like original HTML version!)
    const partElement = screen.querySelector(`[data-part-id="${partId}"]`) as HTMLElement;
    if (!partElement) {
      setParts(prev => prev.map(p => 
        p.id === partId ? { ...p, isDragging: false } : p
      ));
      return;
    }

    // Use getBoundingClientRect for accurate hit detection (like original!)
    const partRect = partElement.getBoundingClientRect();
    const screenRect = screen.getBoundingClientRect();
    
    // Part center in SCREEN coordinates (absolute)
    const partCenterX = partRect.left + partRect.width / 2;
    const partCenterY = partRect.top + partRect.height / 2;
    
    const screenWidth = screen.offsetWidth;
    const screenHeight = screen.offsetHeight;
    
    // Calculate current scale for slot sizing
    const currentScale = Math.min(screenWidth / GAME_DESIGN.width, screenHeight / GAME_DESIGN.height, 1);
    const scaleVal = (px: number) => Math.round(px * currentScale);

    // Find matching slot - use DOM-based hit detection like original
    if (currentPart.definition.type !== 'trash_item') {
      // Find slot DOM element and check hit
      const slotElement = screen.querySelector(`[data-slot-id="${currentPart.definition.id}"]`) as HTMLElement;
      
      let hitSlot = false;
      if (slotElement) {
        const slotRect = slotElement.getBoundingClientRect();
        hitSlot = partCenterX >= slotRect.left && partCenterX <= slotRect.right &&
                  partCenterY >= slotRect.top && partCenterY <= slotRect.bottom;
      }
      
      const slot = hitSlot ? slots.find(s => s.acceptId === currentPart.definition.id && !s.isFilled) : null;

      if (slot) {
        // Correct placement
        const elapsed = performance.now() - currentPart.spawnTime;
        const maxPoints = 250;
        const minPoints = 20;
        const ratio = Math.min(1, elapsed / GAME_CONFIG.partTimeFailMs);
        const points = Math.round(maxPoints - (maxPoints - minPoints) * ratio);

        const newScore = gameStateRef.current.score + points;
        const newPlaced = gameStateRef.current.placedCount + 1;
        updateGameState({ score: newScore, placedCount: newPlaced });

        // Show effect - convert screen coords to relative coords
        setEffects(prev => [...prev, {
          id: generateId(),
          x: partCenterX - screenRect.left,
          y: partCenterY - screenRect.top,
          text: `+${points}`,
          isGood: true,
        }]);

        // Mark slot as filled with animation
        setSlots(prev => prev.map(s => 
          s.id === slot.id ? { ...s, isFilled: true, isAnimating: true } : s
        ));
        
        // Clear animation after it completes
        setTimeout(() => {
          setSlots(prev => prev.map(s => 
            s.id === slot.id ? { ...s, isAnimating: false } : s
          ));
        }, 600);

        // Position part in slot (use scaled dimensions)
        const scaledSlotWidth = scaleVal(slot.width);
        const scaledSlotHeight = scaleVal(slot.height);
        const slotX = (slot.x / 100) * screenWidth - scaledSlotWidth / 2;
        const slotY = (slot.y / 100) * screenHeight;
        
        setParts(prev => prev.map(p => 
          p.id === currentPart.id ? { 
            ...p, 
            x: slotX + (scaledSlotWidth - p.width) / 2,
            y: slotY + (scaledSlotHeight - p.height) / 2,
            isDragging: false, 
            isPlaced: true 
          } : p
        ));

        // Check speed up
        if (newPlaced % 3 === 0 && newPlaced > 0) {
          updateGameState({ speedStage: gameStateRef.current.speedStage + 1 });
          setBanner(t.automationDrop.levelUp);
          setTimeout(() => setBanner(null), 1500);
        }
        return;
      }
    } else {
      // Trash item - check TRAP first, then trash bin (like original!)
      const trapElement = screen.querySelector('[data-retro-trap]') as HTMLElement;
      const trashElement = screen.querySelector('[data-trash-bin]') as HTMLElement;
      
      // Check if dropped on retro trap (bad - in the middle)
      let hitTrap = false;
      if (trapElement && gameStateRef.current.level > 0) {
        const trapRect = trapElement.getBoundingClientRect();
        hitTrap = partCenterX >= trapRect.left && partCenterX <= trapRect.right &&
                  partCenterY >= trapRect.top && partCenterY <= trapRect.bottom;
      }

      if (hitTrap) {
        // Wrong! Dropped on retro trap - show retro message
        setRetroMessage(t.automationDrop.retroMsg || "The 80s want their stuff back!");
        setTimeout(() => setRetroMessage(null), 3000);
        
        // Deduct points
        const newScore = gameStateRef.current.score - 100;
        const newMisses = gameStateRef.current.missesCount + 1;
        updateGameState({ score: newScore, missesCount: newMisses });
        
        // Show effect
        setEffects(prev => [...prev, {
          id: generateId(),
          x: partCenterX - screenRect.left,
          y: partCenterY - screenRect.top,
          text: '-100',
          isGood: false,
        }]);
        
        // Re-add to deck
        shuffledPartsRef.current.push(currentPart.definition);
        updateGameState({ totalParts: shuffledPartsRef.current.length });
        
        // Remove part
        setParts(prev => prev.filter(p => p.id !== currentPart.id));
        
        // Check game over
        if (newMisses >= GAME_CONFIG.maxAllowedMisses) {
          onGameEnd(gameStateRef.current, false);
        }
        return;
      }
      
      // Check if dropped in trash bin (correct)
      let hitTrash = false;
      if (trashElement) {
        const trashRect = trashElement.getBoundingClientRect();
        hitTrash = partCenterX >= trashRect.left && partCenterX <= trashRect.right &&
                   partCenterY >= trashRect.top && partCenterY <= trashRect.bottom;
      }

      if (hitTrash) {
        // Correct trash
        const newScore = gameStateRef.current.score + 250;
        const newPlaced = gameStateRef.current.placedCount + 1;
        updateGameState({ score: newScore, placedCount: newPlaced });

        // Effect position relative to screen
        const trashRect = trashElement!.getBoundingClientRect();
        setEffects(prev => [...prev, {
          id: generateId(),
          x: trashRect.left - screenRect.left + trashRect.width / 2,
          y: trashRect.top - screenRect.top,
          text: '+250',
          isGood: true,
        }]);

        setParts(prev => prev.filter(p => p.id !== currentPart.id));
        return;
      }
    }

    // Wrong placement or no placement - just reset isDragging
    setParts(prev => prev.map(p => 
      p.id === currentPart.id ? { ...p, isDragging: false } : p
    ));
  }, [slots, updateGameState, t.automationDrop.levelUp, t.automationDrop.retroMsg, onGameEnd]);

  // Mouse handlers
  const handleMouseDown = useCallback((e: React.MouseEvent, partId: string) => {
    e.preventDefault();
    handleDragStart(partId, e.clientX, e.clientY);
  }, [handleDragStart]);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    handleDragMove(e.clientX, e.clientY);
  }, [handleDragMove]);

  const handleMouseUp = useCallback(() => {
    handleDragEnd();
  }, [handleDragEnd]);

  // Touch handlers
  const handleTouchStart = useCallback((e: React.TouchEvent, partId: string) => {
    if (e.cancelable) e.preventDefault();
    const touch = e.touches[0];
    if (touch) handleDragStart(partId, touch.clientX, touch.clientY);
  }, [handleDragStart]);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (e.cancelable) e.preventDefault();
    const touch = e.touches[0];
    if (touch) handleDragMove(touch.clientX, touch.clientY);
  }, [handleDragMove]);

  const handleTouchEnd = useCallback(() => {
    handleDragEnd();
  }, [handleDragEnd]);

  // Cleanup effects
  useEffect(() => {
    const cleanup = setInterval(() => {
      setEffects(prev => prev.filter((_, i) => i < 10));
    }, 1000);
    return () => clearInterval(cleanup);
  }, []);

  // Calculate scaled HUD height for top offset
  const scaledHudHeight = scaleValue(HUD_HEIGHT_DESIGN);
  
  // Scaled trash bin dimensions
  const scaledTrashBin = {
    width: scaleValue(TRASH_BIN_DESIGN.width),
    height: scaleValue(TRASH_BIN_DESIGN.height),
    left: scaleValue(TRASH_BIN_DESIGN.left),
    bottom: scaleValue(TRASH_BIN_DESIGN.bottom),
  };

  return (
    <div ref={containerRef} className="absolute inset-0">
      <div 
        ref={screenRef}
        className="absolute inset-0 overflow-hidden cursor-crosshair select-none
                  bg-cover bg-center bg-no-repeat"
        style={{ 
          backgroundImage: 'url(/assets/automation-drop/backgroundleer.png)',
          top: scaledHudHeight,
        }}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onTouchCancel={handleTouchEnd}
      >
        {/* Overlay gradient */}
        <div className="absolute inset-0 bg-gradient-radial from-[rgba(15,23,42,0.4)] to-[rgba(3,7,18,0.7)] pointer-events-none" />

        {/* Slots - scaled dimensions */}
        {slots.map(slot => {
          const scaledWidth = scaleValue(slot.width);
          const scaledHeight = scaleValue(slot.height);
          
          return (
            <div
              key={slot.id}
              data-slot-id={slot.acceptId}
              className={cn(
                "absolute rounded-xl border-2 pointer-events-none",
                // Animation state (success drop) - use custom animation
                slot.isAnimating
                  ? "border-ukonnx-green bg-ukonnx-green/20 border-solid animate-slot-success"
                  // Highlighted (during drag)
                  : slot.isHighlighted 
                    ? "border-ukonnx-green bg-ukonnx-green/10 shadow-[0_0_18px_rgba(34,197,94,0.8)] border-solid"
                    // Filled (after animation) - completely invisible
                    : slot.isFilled
                      ? "border-transparent bg-transparent"
                      // Intro (Level 0)
                      : gameState.level === 0
                        ? "border-gray-400/50 bg-white/5 border-dashed"
                        // Default (Level 1+) - hidden
                        : "border-transparent border-dashed",
                // Transition for smooth animations (not during success animation)
                !slot.isAnimating && "transition-all duration-300"
              )}
              style={{
                left: `calc(${slot.x}% - ${scaledWidth / 2}px)`,
                top: `${slot.y}%`,
                width: scaledWidth,
                height: scaledHeight,
              }}
            />
          );
        })}

        {/* Trash Bin - scaled */}
        <div 
          data-trash-bin="true"
          className={cn(
            "absolute rounded-xl border-2 border-dashed",
            "flex flex-col items-center justify-center transition-all pointer-events-none",
            draggingTrashItem
              ? "border-ukonnx-green bg-ukonnx-green/15 shadow-[0_0_20px_rgba(34,197,94,0.4)]"
              : gameState.level === 0
                ? "border-gray-400/50 bg-white/5"  // Level 0 (intro): visible
                : "border-white/10"  // Level 1+: subtle
          )}
          style={{
            left: scaledTrashBin.left,
            bottom: scaledTrashBin.bottom,
            width: scaledTrashBin.width,
            height: scaledTrashBin.height,
          }}
        >
          <span 
            className="text-white/30"
            style={{ fontSize: Math.max(10, scaleValue(14)) }}
          >
            {t.automationDrop.trashLabel}
          </span>
        </div>

        {/* Retro Trap Zone (red zone in center) - visible in intro and when dragging trash item */}
        <div
          data-retro-trap="true"
          className={cn(
            "absolute rounded-full border-2 border-dashed transition-all pointer-events-none",
            "flex items-center justify-center",
            draggingTrashItem
              ? trapHover
                ? "border-red-500 bg-red-500/20 shadow-[0_0_30px_rgba(239,68,68,0.5)]"
                : "border-red-500/30 bg-red-500/5 animate-pulse"
              : gameState.level === 0
                ? "border-red-500/30 bg-red-500/5"  // Level 0 (intro): show trap zone
                : "border-transparent"  // Level 1+: hidden until dragging trash item
          )}
          style={{
            left: '50%',
            top: `${RETRO_TRAP_DESIGN.topPercent}%`,
            transform: 'translate(-50%, -50%)',
            width: scaleValue(RETRO_TRAP_DESIGN.width),
            height: scaleValue(RETRO_TRAP_DESIGN.height),
          }}
        >
          {(draggingTrashItem || gameState.level === 0) && (
            <span className="text-red-500/60 font-bold text-sm">No 80s!</span>
          )}
        </div>

        {/* Falling Parts */}
        {parts.map(part => (
          <div
            key={part.id}
            data-part-id={part.id}
            className={cn(
              "absolute cursor-grab flex items-center justify-center z-20",
              part.isDragging && "cursor-grabbing scale-110 z-50 drop-shadow-2xl",
              part.isPlaced && "pointer-events-none"
            )}
            style={{
              left: part.x,
              top: part.y,
              width: part.width,
              height: part.height,
            }}
            onMouseDown={(e) => handleMouseDown(e, part.id)}
            onTouchStart={(e) => handleTouchStart(e, part.id)}
          >
            <img 
              src={part.definition.image} 
              alt={part.definition.label}
              className="max-w-full max-h-full object-contain pointer-events-none"
              draggable={false}
            />
          </div>
        ))}

        {/* Effects - scaled font */}
        {effects.map(effect => (
          <div
            key={effect.id}
            className={cn(
              "absolute font-bold pointer-events-none animate-float-up z-50",
              effect.isGood ? "text-ukonnx-green" : "text-accent-red"
            )}
            style={{ 
              left: effect.x - scaleValue(20), 
              top: effect.y,
              fontSize: Math.max(14, scaleValue(20)),
            }}
          >
            {effect.text}
          </div>
        ))}

        {/* Banner - scaled */}
        {banner && (
          <div 
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50
                        bg-[rgba(6,78,59,0.9)] text-ukonnx-green-light rounded-full
                        font-bold whitespace-nowrap animate-scale-in
                        shadow-[0_0_0_4px_rgba(34,197,94,0.5),0_20px_50px_rgba(0,0,0,0.8)]"
            style={{
              padding: `${scaleValue(20)}px ${scaleValue(40)}px`,
              fontSize: Math.max(16, scaleValue(24)),
            }}
          >
            {banner}
          </div>
        )}

        {/* Retro Message (red error message) */}
        {retroMessage && (
          <div 
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-100
                        bg-red-500 text-white rounded-xl font-bold text-center
                        animate-scale-in pointer-events-none
                        border-4 border-red-900"
            style={{
              padding: `${scaleValue(20)}px`,
              fontSize: Math.max(18, scaleValue(24)),
            }}
          >
            {retroMessage}
          </div>
        )}
      </div>
    </div>
  );
}
